import { ReactNode } from 'react';
import { CSS, styled } from '@ghq-abi/design-system';
import { IdpFeedbackStatusEnum, SurveyStatusEnum } from 'abi-lcm-common-types';

export const EmployeeProfileSeverity = {
  WARNING: 'warning',
  ERROR: 'error',
} as const;

export type EmployeeProfileSeverityType =
  (typeof EmployeeProfileSeverity)[keyof typeof EmployeeProfileSeverity];

type ProfileContainerProps = {
  severity?: EmployeeProfileSeverityType;
  active?: boolean;
  surveyStatus?: SurveyStatusEnum;
  feedbackStatus?: IdpFeedbackStatusEnum;
  children: ReactNode;
  css?: CSS;
  disabled?: boolean;
  as?: 'button' | 'div';
  title?: string;
  onClick?: () => void;
};

const Container = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '$sm',
  borderRadius: '$2',
  background: 'var(--gray-3, #F3F3F3)',

  variants: {
    clickable: {
      true: {
        cursor: 'pointer',
        transition: '$quickly-ease',
        '&:hover': { filter: 'brightness(0.92 )' },
        '&:active': { filter: 'brightness(0.92)' },
      },
    },
    active: {
      true: {
        filter: 'brightness(0.90)',
      },
    },
    severity: {
      warning: {
        background: '#FFFCE8',
      },
      error: {
        background: '#FFD3D4',
      },
    },
    surveyStatus: {
      [SurveyStatusEnum.PENDING_MANAGERS_APPROVAL]: {
        bg: '#FFFCE8',
      },
      [SurveyStatusEnum.SUBMITTED]: {
        bg: '#EEFFF4',
      },
      [SurveyStatusEnum.FEEDBACK_GIVEN]: {
        bg: '#F7CFB4',
      },
      [SurveyStatusEnum.NOT_SUBMITTED]: {
        bg: '$gray300',
      },
    },
    feedbackStatus: {
      [IdpFeedbackStatusEnum.FEEDBACK_GIVEN]: {
        bg: '#EBFCD5',
      },
      [IdpFeedbackStatusEnum.NOT_REQUESTED]: {
        bg: '#F5F6F7',
      },
      [IdpFeedbackStatusEnum.FEEDBACK_REQUESTED]: {
        bg: '#FBF2B3',
      },
    },
  },
});

export function ProfileContainer({
  severity,
  children,
  active,
  surveyStatus,
  feedbackStatus,
  css,
  disabled,
  as = 'div',
  title,
  onClick,
}: ProfileContainerProps) {
  return (
    <Container
      as={as}
      disabled={disabled}
      title={title}
      severity={severity}
      active={active}
      surveyStatus={surveyStatus}
      feedbackStatus={feedbackStatus}
      css={css}
      onClick={onClick}
      clickable={!!onClick}
    >
      {children}
    </Container>
  );
}
